import { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, Box, Typography, Button, IconButton, Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { COLORS, FONT_HEADING, BUTTON_HOVER_SX } from '../../talkTreeShared/Tree/constants/theme';

const BLUE = COLORS.primary;

const StepNumber = ({ n }) => (
  <Box
    sx={{
      width: 56,
      height: 56,
      borderRadius: '50%',
      backgroundColor: COLORS.primaryLight,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#fff' }}>
      {n}
    </Typography>
  </Box>
);

const TALK_TREE_GUIDE_URL = 'https://www.talktrees.ai/playbooks/how-to-introduce-talk-trees';

const SECTIONS = {
  main: {
    title: 'How to stream your classroom',
    buttonLabel: "I'm ready to start streaming",
    steps: [
      {
        title: 'Set up suggestions',
        bullets: [
          'Streaming can be done from any device with a built-in microphone, including Apple or Android phones, tablets, and laptops.',
          "Place your main device in a central location, such as a desk or the middle of the room. The goal is for your device to pick up all of your students' conversations as clearly as possible.",
          'You can add other devices if you have a large classroom and need more coverage.',
        ],
      },
      {
        title: 'Fill out the session form.',
        body: 'At minimum, provide a session title. You can also add a description, which can help you remember the class period and other details about the session.',
        bold: 'Click "Create Session."',
      },
      {
        title: 'Add optional additional devices',
        body: [
          'If you are streaming from multiple devices, you may want to use a laptop to project onto a screen so that you can display the QR code provided for other devices to connect to the same stream. If you are only recording from one device, you can skip the QR code step. See "',
          { link: 'Help me stream from additional devices', section: 'devices' },
          '" for directions on adding new devices.',
        ],
        bold: 'Click "Start streaming" whenever you\'re ready.',
      },
      {
        title: 'To end your session',
        body: 'When you\'re finished, click "Stop Stream and Generate Talk Tree" to generate your talk tree.',
      },
      {
        title: 'Use your Talk Tree to reflect and celebrate',
        body: 'Once you see the Talk Tree Ready status, click it to explore your Talk Tree. Use the tree with your class to celebrate and reflect on student talk. Use multiple Talk Trees to compare how talk changes in your class over time.',
        link: { label: 'How to use your Talk Tree →', href: TALK_TREE_GUIDE_URL },
      },
    ],
  },
  devices: {
    title: 'How to stream from additional devices',
    buttonLabel: "I'm ready to add devices",
    steps: [
      {
        title: 'Click on "Add Device" to expand the QR code.',
      },
      {
        title: 'Have any additional devices scan the QR code to join the stream.',
        body: 'No login is needed, and the same QR code works for every device.',
      },
      {
        title: 'When prompted, make sure to enable the microphone on each device.',
        body: 'The prompt language will differ slightly between Android and Apple devices, but be sure to allow microphone access.',
      },
      {
        title: 'Once devices are added, each one will be listed on screen.',
        body: 'You can pause or resume individual device streams, or pause and resume all recordings at once.',
        boldLead: 'Note that a device may go to sleep during recording.',
        rest: ' It should continue recording, but check periodically for any devices that appear idle or asleep, and wake them if needed.',
      },
      {
        title: 'To end your session',
        body: 'When you\'re finished, click "Stop Stream and Generate Talk Tree" to generate your talk tree.',
      },
    ],
  },
};

const BODY_SX = { fontSize: '15px', color: COLORS.text, lineHeight: 1.6 };

const linkSx = {
  fontSize: 'inherit',
  lineHeight: 'inherit',
  verticalAlign: 'baseline',
  color: BLUE,
  textDecoration: 'underline',
  cursor: 'pointer',
};

export default function StreamHelpDialog({ open, onClose, section = 'main' }) {
  // The dialog can switch between sections itself (step 3 link -> devices).
  const [view, setView] = useState(section);

  // Reset to the requested section whenever the dialog is (re)opened.
  useEffect(() => {
    if (open) setView(section);
  }, [open, section]);

  const content = SECTIONS[view] || SECTIONS.main;

  // In-dialog link that switches to another section.
  const renderSectionLink = (label, target) => (
    <Link
      component="button"
      type="button"
      onClick={() => setView(target)}
      sx={linkSx}
    >
      {label}
    </Link>
  );

  // External link that opens in a new tab.
  const renderExternalLink = (label, href) => (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      sx={linkSx}
    >
      {label}
    </Link>
  );

  const renderBody = (body) => {
    if (!Array.isArray(body)) return body;
    return body.map((part, i) => (
      typeof part === 'string'
        ? <span key={i}>{part}</span>
        : <span key={i}>{renderSectionLink(part.link, part.section)}</span>
    ));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          backgroundColor: COLORS.page,
          p: { xs: 2, sm: 3 },
        },
      }}
    >
      <IconButton
        onClick={onClose}
        aria-label="Close"
        sx={{ position: 'absolute', right: 16, top: 16, color: '#111827' }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ pt: 1 }}>
        <Typography
          sx={{
            fontFamily: FONT_HEADING,
            fontSize: { xs: '26px', sm: '32px' },
            fontWeight: 400,
            color: BLUE,
            mb: 4,
          }}
        >
          {content.title}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {content.steps.map((step, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 3 }}>
              <StepNumber n={i + 1} />
              <Box sx={{ flex: 1, pt: 1 }}>
                <Typography sx={{ fontSize: '17px', fontWeight: 700, color: '#111827', mb: (step.bullets || step.body || step.boldLead) ? 0.5 : 0 }}>
                  {step.title}
                </Typography>

                {step.bullets && (
                  <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {step.bullets.map((b, j) => (
                      <Typography key={j} component="li" sx={BODY_SX}>
                        {b}
                      </Typography>
                    ))}
                  </Box>
                )}

                {step.body && (
                  <Typography sx={{ ...BODY_SX, mb: (step.bold || step.boldLead || step.link) ? 1 : 0 }}>
                    {renderBody(step.body)}
                  </Typography>
                )}

                {step.boldLead && (
                  <Typography sx={BODY_SX}>
                    <Typography component="span" sx={{ fontWeight: 700, color: '#111827' }}>
                      {step.boldLead}
                    </Typography>
                    {step.rest}
                  </Typography>
                )}

                {step.bold && (
                  <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#111827' }}>
                    {step.bold}
                  </Typography>
                )}

                {step.link && (
                  <Typography sx={{ fontSize: '15px', lineHeight: 1.6 }}>
                    {renderExternalLink(step.link.label, step.link.href)}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>

        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            mt: 4,
            py: 1.4,
            px: 3,
            fontSize: '15px',
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: '4px',
            backgroundColor: COLORS.primaryDark,
            boxShadow: 'none',
            '&:hover': BUTTON_HOVER_SX,
          }}
        >
          {content.buttonLabel}
        </Button>
      </DialogContent>
    </Dialog>
  );
}